import { StandardEvents } from '@shopify/events';

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const revealTabs = container => {
  if (!container || container.dataset.revealed || reducedMotion()) return;
  container.classList.add('ky-pdp-tabs-enter');
  const observer=new IntersectionObserver(entries=>{ if(entries.some(entry=>entry.isIntersecting)) { container.dataset.revealed='true'; observer.disconnect(); } },{threshold:.2});
  observer.observe(container.querySelector('[role=tablist]') || container);
  return observer;
};
class KyProductPage extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.gallery = this.querySelector('[data-gallery]');
    this.items = [...this.querySelectorAll('[data-media-id]')];
    this.dialog = this.querySelector('dialog');
    this.sheet = this.querySelector('.ky-pdp__sheet');
    this.dataset.scrolled = String(window.scrollY > 0);
    this.style.setProperty('--ky-pdp-page-width', `${document.documentElement.clientWidth}px`);
    window.addEventListener('scroll', () => {
      if ((this.dialog.open || this.sheet.open) && window.scrollY !== this.modalScrollY) {
        window.scrollTo({ top:this.modalScrollY, behavior:'instant' }); return;
      }
      this.dataset.scrolled = String(window.scrollY > 0);
    }, { ...options, passive: true });
    const preventBackdropScroll = event => {
      const modal = this.sheet.open ? this.sheet : this.dialog.open ? this.dialog : null;
      if (modal && !event.target.closest('.ky-pdp__sheet-content, .ky-pdp__zoom-stage')) event.preventDefault();
    };
    document.addEventListener('wheel', preventBackdropScroll, { ...options, passive:false });
    document.addEventListener('touchmove', preventBackdropScroll, { ...options, passive:false });
    window.addEventListener('resize', () => { this.style.setProperty('--ky-pdp-page-width', `${document.documentElement.clientWidth}px`); this.dialog.close(); this.clearInlineZoom(); }, options);
    this.sheet.addEventListener('cancel', event => { event.preventDefault(); this.closeInformation(); }, options);
    this.sheet.addEventListener('close', () => this.informationOrigin?.focus({ preventScroll: true }), options);
    this.sheet.addEventListener('click', event => { if (event.target === this.sheet) this.closeInformation(); }, options);
    const cursor = this.querySelector('.ky-pdp__zoom-cursor');
    this.gallery.addEventListener('pointermove', event => {
      const visible = event.pointerType === 'mouse' && event.target.closest('[data-zoom]') && matchMedia('(min-width:768px)').matches;
      cursor.hidden = !visible;
      if (visible) {
        cursor.style.left = `${event.clientX}px`; cursor.style.top = `${event.clientY}px`;
        const image = event.target.closest('[data-zoom]');
        if (image !== this.inlineZoom) this.clearInlineZoom();
        else this.positionInlineZoom(event);
      }
    }, options);
    this.gallery.addEventListener('pointerleave', () => { cursor.hidden = true; this.clearInlineZoom(); }, options);
    this.gallery.addEventListener('keydown', event => { if (event.key === 'Escape') this.clearInlineZoom(); }, options);
    const stage = this.querySelector('.ky-pdp__zoom-stage');
    this.zoomPointers = new Map();
    stage.addEventListener('pointerdown', event => {
      cancelAnimationFrame(this.zoomAnimation);
      this.zoomVelocity = null; this.zoomLastMove = { x:event.clientX, y:event.clientY, time:performance.now() };
      this.zoomPointers.set(event.pointerId, { x:event.clientX, y:event.clientY });
      this.zoomGesture = this.zoomGestureSnapshot();
      stage.setPointerCapture(event.pointerId);
    }, options);
    stage.addEventListener('pointermove', event => {
      if (!this.zoomPointers.has(event.pointerId)) return;
      this.zoomPointers.set(event.pointerId, { x:event.clientX, y:event.clientY });
      const next = this.zoomGestureSnapshot(), start = this.zoomGesture;
      if (!start) return;
      const scale = Math.max(1,Math.min(4,start.scale * (next.distance && start.distance ? next.distance/start.distance : 1)));
      const rect = this.querySelector('.ky-pdp__zoom-frame').getBoundingClientRect();
      const ratio = scale/start.scale;
      this.zoomState = { scale, x:next.x-rect.left-(start.x-rect.left-start.left)*ratio, y:next.y-rect.top-(start.y-rect.top-start.top)*ratio };
      if (this.zoomPointers.size === 1 && this.zoomLastMove) {
        const time=performance.now(), dt=Math.max(1,time-this.zoomLastMove.time), dx=event.clientX-this.zoomLastMove.x, dy=event.clientY-this.zoomLastMove.y;
        this.zoomVelocity={ x:dx/dt*2*scale*20*Math.max(1,Math.min(2,2-rect.width/innerWidth)), y:dy/dt*2*scale*20*Math.max(1,Math.min(2,2-rect.height/innerHeight)), speed:Math.hypot(dx,dy)/dt*2 };
        this.zoomLastMove={x:event.clientX,y:event.clientY,time};
      }
      this.renderZoom();
    }, options);
    const endPointer = event => {
      this.zoomPointers.delete(event.pointerId); this.zoomGesture = this.zoomGestureSnapshot();
      if (!this.zoomPointers.size && this.zoomVelocity && performance.now()-this.zoomLastMove.time<100 && !reducedMotion()) {
        const velocity=this.zoomVelocity, duration=1600-1600/Math.max(1,velocity.speed), state={...this.zoomState}, start=performance.now();
        const animate=time=>{ if(!duration)return; const progress=Math.min(1,(time-start)/Math.max(400,duration)), eased=1-Math.pow(1-progress,4); this.zoomState={scale:state.scale,x:state.x+velocity.x*eased,y:state.y+velocity.y*eased};this.renderZoom();if(progress<1)this.zoomAnimation=requestAnimationFrame(animate); };
        this.zoomAnimation=requestAnimationFrame(animate);
      }
    };
    stage.addEventListener('pointerup', endPointer, options);
    stage.addEventListener('pointercancel', endPointer, options);
    stage.addEventListener('wheel', event => { event.preventDefault(); const factor=Math.max(.2,Math.min(.99,2-innerWidth/this.querySelector('.ky-pdp__zoom-frame').clientWidth)); this.scaleZoom(this.zoomState.scale*(1+5*(event.deltaY<0?1:-1)*(1-factor)/20),event.clientX,event.clientY); }, { ...options, passive:false });
    stage.addEventListener('dblclick', event => { event.preventDefault(); this.scaleZoom(this.zoomState.scale*(2-innerWidth*.0001),event.clientX,event.clientY,200); }, options);
    this.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.action !== 'add') return;
      event.promise?.then(({detail}) => {
        if (!detail?.didError && this.isConnected) setTimeout(() => document.querySelector('#cart-drawer')?.open?.(), 600);
      }).catch(() => {});
    }, options);
    this.active = 0;
    this.initializeDetails();
    this.addEventListener('click', this.click, options);
    this.gallery.addEventListener('scroll', this.updateGallery, { ...options, passive: true });
    this.dialog.addEventListener('close', () => { this.zoomOrigin?.focus({ preventScroll: true }); }, options);
    this.dialog.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); this.showZoom(this.zoomIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
    }, options);
    this.closest('.shopify-section').addEventListener(StandardEvents.productSelect, event => {
      event.promise?.then(({ detail }) => {
        if (!this.isConnected) return;
        const id = detail?.resource?.featured_media?.id;
        const index = this.items.findIndex(item => item.dataset.mediaId === String(id));
        if (index >= 0) this.selectImage(index);
        const incoming = detail?.html?.querySelector('.ky-pdp .quantity-selector input');
        const quantity = this.querySelector('.quantity-selector input');
        if (incoming && quantity) {
          quantity.disabled = incoming.disabled;
          if (incoming.disabled) this.querySelectorAll('.quantity-selector button').forEach(button => { button.disabled = true; });
          else this.querySelector('quantity-selector-component')?.updateConstraints(incoming.min, incoming.max || null, incoming.step);
        }
      }).catch(() => {});
    }, options);
    this.resize = new ResizeObserver(this.updateGallery); this.resize.observe(this.gallery);
    this.updateGallery();
  }
  disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); this.tabsObserver?.disconnect(); clearTimeout(this.galleryTimer); cancelAnimationFrame(this.zoomAnimation); }
  clearInlineZoom() {
    if (this.inlineZoom) { this.inlineZoom.removeAttribute('data-magnified'); this.inlineZoom.style.backgroundImage = ''; this.inlineZoom.setAttribute('aria-pressed','false'); }
    this.inlineZoom = null;
    this.querySelector('.ky-pdp__zoom-cursor')?.removeAttribute('data-magnified');
  }
  positionInlineZoom(event) {
    if (!this.inlineZoom) return;
    const rect = this.inlineZoom.getBoundingClientRect();
    const x = event.detail === 0 && event.type === 'click' ? 50 : (event.clientX-rect.left)/rect.width*100;
    const y = event.detail === 0 && event.type === 'click' ? 50 : (event.clientY-rect.top)/rect.height*100;
    this.inlineZoom.style.backgroundPosition = `${x}% ${y}%`;
  }
  zoomGestureSnapshot() {
    const points = [...this.zoomPointers.values()];
    if (!points.length || !this.zoomState) return null;
    const [a,b=a] = points;
    return { x:(a.x+b.x)/2, y:(a.y+b.y)/2, distance:Math.hypot(b.x-a.x,b.y-a.y), scale:this.zoomState.scale, left:this.zoomState.x, top:this.zoomState.y };
  }
  renderZoom() {
    const { x,y,scale } = this.zoomState;
    this.querySelector('[data-zoom-image]').style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  }
  scaleZoom(requested, clientX, clientY, duration=0) {
    cancelAnimationFrame(this.zoomAnimation);
    const scale = Math.max(1,Math.min(4,requested)), rect = this.querySelector('.ky-pdp__zoom-frame').getBoundingClientRect();
    const ratio = scale/this.zoomState.scale, x = clientX-rect.left, y = clientY-rect.top;
    const target={ scale, x:x-(x-this.zoomState.x)*ratio, y:y-(y-this.zoomState.y)*ratio };
    if (!duration || reducedMotion()) { this.zoomState=target;this.renderZoom();return; }
    const from={...this.zoomState}, start=performance.now();
    const animate=time=>{ const progress=Math.min(1,(time-start)/duration), eased=-Math.cos(progress*Math.PI)/2+.5; this.zoomState={scale:from.scale+(target.scale-from.scale)*eased,x:from.x+(target.x-from.x)*eased,y:from.y+(target.y-from.y)*eased};this.renderZoom();if(progress<1)this.zoomAnimation=requestAnimationFrame(animate); };
    this.zoomAnimation=requestAnimationFrame(animate);
  }
  initializeDetails() {
    const container = this.querySelector('[data-detail-tabs]');
    const panels = [...container.querySelectorAll('[data-detail-panel]')];
    const list = container.querySelector('[role=tablist]');
    panels.forEach((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button'; tab.textContent = panel.dataset.title;
      tab.id = `${panel.id}-tab`; tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id); tab.dataset.detailTab = panel.id;
      tab.setAttribute('aria-selected', String(index === 0)); tab.tabIndex = index === 0 ? 0 : -1;
      panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id); panel.hidden = index !== 0;
      list.append(tab);
    });
    if (panels.length) this.tabsObserver=revealTabs(container);
    list.addEventListener('keydown', event => {
      const tabs = [...list.querySelectorAll('[role=tab]')], index = tabs.indexOf(event.target);
      if (index < 0 || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[next].focus();
    }, { signal: this.controller.signal });
  }
  selectDetail(id) {
    this.querySelectorAll('[data-detail-tab]').forEach(tab => { const selected = tab.dataset.detailTab === id; tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1; });
    this.querySelectorAll('[data-detail-panel]').forEach(panel => { panel.hidden = panel.id !== id; });
  }
  openInformation(origin, title, fragment) {
    clearTimeout(this.sheetCloseTimer); this.informationOrigin = origin;
    this.sheet.dataset.kind = 'information';
    this.sheet.querySelector('[data-information-close] i').textContent = '\uf154';
    this.sheet.querySelector('h2').textContent = title;
    this.sheet.querySelector('.ky-pdp__sheet-content').replaceChildren(fragment);
    this.modalScrollY = window.scrollY; this.sheet.showModal();
    requestAnimationFrame(() => requestAnimationFrame(() => this.sheet.classList.add('is-active')));
  }
  openWishlist(origin) {
    this.openInformation(origin, 'Save to wishlist', this.querySelector('[data-wishlist-content]').content.cloneNode(true));
    this.sheet.dataset.kind = 'wishlist';
    this.sheet.querySelector('[data-information-close] i').textContent = '\uf155';
  }
  closeInformation() {
    if (!this.sheet.open) return;
    this.sheet.classList.remove('is-active');
    clearTimeout(this.sheetCloseTimer);
    this.sheetCloseTimer = setTimeout(() => this.sheet.close(), reducedMotion() ? 0 : 400);
  }
  selectImage(index) {
    index = Math.max(0, Math.min(index, this.items.length - 1));
    const item = this.items[index]; if (!item) return;
    const desktop = matchMedia('(min-width:768px)').matches;
    this.gallery.scrollTo({ left: desktop ? 0 : item.offsetLeft, top: desktop ? item.offsetTop : 0, behavior: reducedMotion() ? 'instant' : 'smooth' });
  }
  updateGallery = () => {
    this.clearInlineZoom();
    const vertical = matchMedia('(min-width:768px)').matches;
    const current = vertical ? this.gallery.scrollTop : this.gallery.scrollLeft;
    let closest = 0, distance = Infinity;
    this.items.forEach((item, index) => { const d = Math.abs((vertical ? item.offsetTop : item.offsetLeft) - current); if (d < distance) { closest = index; distance = d; } });
    this.active = closest;
    this.querySelectorAll('[data-gallery-index]').forEach((button, index) => button.setAttribute('aria-current', String(index === closest)));
    const previous = this.querySelector('[data-gallery-step="-1"]'), next = this.querySelector('[data-gallery-step="1"]');
    if (previous) previous.disabled = current < 1;
    if (next) next.disabled = current >= (vertical ? this.gallery.scrollHeight - this.gallery.clientHeight : this.gallery.scrollWidth - this.gallery.clientWidth) - 1;
    const progress = this.querySelector('.ky-pdp__gallery-progress span');
    if (progress) progress.style.top = `${Math.max(0,Math.min(1,current / Math.max(1,this.gallery.scrollHeight-this.gallery.clientHeight))) * 92}px`;
    if (vertical) { const nav = this.querySelector('.ky-pdp__gallery-navigation'); nav?.classList.add('is-scrolling'); clearTimeout(this.galleryTimer); this.galleryTimer = setTimeout(() => nav?.classList.remove('is-scrolling'),1500); }
  };
  showZoom(index) {
    const images = this.items.map((item, i) => ({ element: item.querySelector('.ky-pdp__image-button img'), index: i })).filter(item => item.element);
    if (!images.length) return;
    const image = images[(index + images.length) % images.length];
    this.zoomIndex = (index + images.length) % images.length;
    const target = this.querySelector('[data-zoom-image]');
    target.src = image.element.src.replace(/width=\d+/, 'width=2000');
    target.alt = image.element.alt;
    this.zoomPointers.clear();
    this.zoomState = { scale:1.75, x:-145, y:-180 }; this.renderZoom();
    if (!this.dialog.open) { this.modalScrollY = window.scrollY; this.dialog.showModal(); }
  }
  click = event => {
    const button = event.target.closest('button');
    if (button?.hasAttribute('data-information-open')) {
      this.openInformation(button, button.querySelector('span').textContent, button.parentElement.querySelector('template').content.cloneNode(true));
    }
    if (button?.hasAttribute('data-wishlist-open')) this.openWishlist(button);
    if (button?.hasAttribute('data-detail-tab')) this.selectDetail(button.dataset.detailTab);
    if (button?.hasAttribute('data-detail-link')) {
      this.selectDetail(`ky-detail-${button.dataset.detailLink}`);
      this.querySelector('[data-detail-tabs]').scrollIntoView({ behavior:reducedMotion() ? 'instant' : 'smooth', block:'start' });
    }
    if (button?.hasAttribute('data-share')) {
      const data = { title:this.querySelector('h1').textContent, url:location.href };
      if (navigator.share) navigator.share(data).catch(() => {});
      else navigator.clipboard?.writeText(data.url).then(() => {
        const status=this.querySelector('[data-share-status]'); status.firstElementChild.textContent='Link copied'; status.classList.remove('is-copied'); void status.offsetWidth; status.classList.add('is-copied');
      }).catch(() => { const status=this.querySelector('[data-share-status]'); status.firstElementChild.textContent=data.url; status.classList.add('is-copied'); });
    }
    if (button?.hasAttribute('data-information-close')) this.closeInformation();
    if (button?.hasAttribute('data-gallery-index')) this.selectImage(Number(button.dataset.galleryIndex));
    if (button?.hasAttribute('data-gallery-step')) {
      if (matchMedia('(min-width:768px)').matches) this.gallery.scrollBy({ top:Number(button.dataset.galleryStep)*150, behavior:reducedMotion() ? 'instant' : 'smooth' });
      else this.selectImage(this.active + Number(button.dataset.galleryStep));
    }
    if (button?.hasAttribute('data-zoom')) {
      this.zoomOrigin = button;
      if (matchMedia('(min-width:768px)').matches) {
        if (this.inlineZoom === button) this.clearInlineZoom();
        else {
          this.clearInlineZoom(); this.inlineZoom = button;
          button.style.backgroundImage = `url("${button.dataset.zoomSrc}")`;
          button.dataset.magnified = ''; button.setAttribute('aria-pressed','true');
          this.querySelector('.ky-pdp__zoom-cursor').dataset.magnified = '';
          this.positionInlineZoom(event);
        }
      } else {
        this.querySelector('.ky-pdp__zoom-cursor').hidden = true;
        const images = [...this.querySelectorAll('[data-zoom]')];
        this.showZoom(images.indexOf(button));
      }
    }
    if (button?.hasAttribute('data-zoom-close')) this.dialog.close();
    if (button?.hasAttribute('data-zoom-step')) this.showZoom(this.zoomIndex + Number(button.dataset.zoomStep));
  };
}

class KyPdpRecommendations extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    if (this.dataset.performed !== 'true') this.load(); else this.bind();
  }
  disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); this.tabsObserver?.disconnect(); }
  async load() {
    try {
      const response = await fetch(this.dataset.url, { signal: this.controller.signal });
      if (!response.ok) throw Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('ky-pdp-recommendations');
      if (incoming && this.isConnected) { this.innerHTML = incoming.innerHTML; this.dataset.performed = 'true'; this.bind(); }
    } catch (error) { if (error.name !== 'AbortError') this.hidden = true; }
  }
  bind() {
    this.track = this.querySelector('.ky-pdp-recommendations__track');
    if (!this.track) { this.hidden = true; return; }
    this.tabsObserver=revealTabs(this);
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => {
      const tab = event.target.closest('[data-recommendation-tab]');
      if (tab) this.selectTab(tab);
      if (event.target.closest('[data-wishlist-open]')) { document.querySelector('ky-product-page')?.openWishlist(event.target.closest('button')); return; }
      const button = event.target.closest('[data-recommendation-step]');
      if (!button) return;
      this.track.scrollBy({ left: this.track.clientWidth * Number(button.dataset.recommendationStep), behavior: reducedMotion() ? 'instant' : 'smooth' });
    }, options);
    this.querySelector('[role=tablist]')?.addEventListener('keydown', event => {
      const tabs = [...this.querySelectorAll('[data-recommendation-tab]')], index = tabs.indexOf(event.target);
      if (index < 0 || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const next = (index+(event.key === 'ArrowRight' ? 1 : -1)+tabs.length)%tabs.length;
      tabs[next].focus();
    }, options);
    this.track.addEventListener('scroll', this.update, { ...options, passive: true });
    this.resize = new ResizeObserver(this.update); this.resize.observe(this.track); this.update();
  }
  selectTab(selected) {
    this.querySelectorAll('[data-recommendation-tab]').forEach(tab => { const active=tab===selected; tab.setAttribute('aria-selected',String(active)); tab.tabIndex=active?0:-1; });
    this.querySelectorAll('[data-recommendation-panel]').forEach(panel => { panel.hidden=panel.id!==selected.dataset.recommendationTab; });
    this.track=this.querySelector('#'+selected.dataset.recommendationTab);
    this.track.scrollLeft=0;
  }
  update = () => {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const previous = this.querySelector('[data-recommendation-step="-1"]'), next = this.querySelector('[data-recommendation-step="1"]');
    if(previous) previous.disabled = this.track.scrollLeft < 1;
    if(next) next.disabled = this.track.scrollLeft >= max - 1;
    const progress = this.querySelector('.ky-pdp-recommendations__progress span');
    if (!progress) return;
    const fraction = this.track.clientWidth / this.track.scrollWidth;
    progress.style.width = `${fraction * 100}%`;
    progress.style.setProperty('--progress', `${this.track.scrollLeft / this.track.clientWidth * 100}%`);
  };
}
if (!customElements.get('ky-product-page')) customElements.define('ky-product-page', KyProductPage);
if (!customElements.get('ky-pdp-recommendations')) customElements.define('ky-pdp-recommendations', KyPdpRecommendations);
